import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useDialogConfirm } from '@src/components/Common/Dialog';
import CallMediaSingleton from '@src/logic/call/callMedia/CallMediaSingleton';
import generateRoomId from '@src/logic/call/generateRoomId';
import {
  RequestOutgoingContactCallRequest,
  useRequestOutgoingContactCallMutation,
} from '@src/reducers/callsAPI';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import useHandleCallMediaError from './useHandleCallMediaError';

type StartContactCallParams = Omit<
  RequestOutgoingContactCallRequest,
  'callContext'
>;

const useStartOutgoingContactCallMedia = () => {
  const { formatMessage } = useIntl();
  const dialogConfirm = useDialogConfirm();
  const handleCallMediaError = useHandleCallMediaError();

  const [
    requestOutgoingContactCallMutation,
    { error: errorOutgoingCall, isLoading: isLoadingOutgoingCall },
  ] = useRequestOutgoingContactCallMutation({ fixedCacheKey: 'calls' });

  const startCall = async (
    params: StartContactCallParams
  ): Promise<boolean> => {
    if (isLoadingOutgoingCall) return false;

    const roomId = generateRoomId();

    try {
      const { sessionId, handleId } = await CallMediaSingleton.connect();
      if (!sessionId || !handleId) return false;

      await CallMediaSingleton.joinRoom(roomId);
      const payload: RequestOutgoingContactCallRequest = {
        ...params,
        callContext: {
          handleId,
          sessionId,
          roomId,
        },
      };

      await requestOutgoingContactCallMutation(payload).unwrap();
    } catch (error) {
      handleCallMediaError(error);
      await CallMediaSingleton.leaveRoom();
      return false;
    }

    return true;
  };

  useEffect(() => {
    const status = (errorOutgoingCall as FetchBaseQueryError)?.status;
    if (status === 400 || status === 500)
      dialogConfirm({
        disableBackdropClose: true,
        title: formatMessage({
          id: 'Call.Error.ContactCallFailed.Title',
          defaultMessage: 'Error al iniciar llamada',
        }),
        description: formatMessage({
          id: 'Call.Error.ContactCallFailed.Description',
          defaultMessage:
            'No fue posible establecer la comunicación con el contacto.',
        }),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorOutgoingCall]);

  return startCall;
};

export default useStartOutgoingContactCallMedia;
